import { Box, Button, Collapse, Divider, Typography } from "@material-ui/core"
import Drawer from "@material-ui/core/Drawer"
import NextLink from "common/bit/NextLink"
import { SideDrawerMobile } from "common/SiteLinks"
import { useGlobalContext } from "contexts/GlobalContext"
import Image from "next/image"
import React from "react"
import ButtonBaseBlank from "reusable/ButtonBaseBlank"
import styled from "styled-components"
import colors from "theme/colors"
import { HEADER_HEIGHT_M, StyledCol, TypographyRtl } from "theme/shared"
import HeaderTopButtons from "../HeaderTopButtons"
import SubFolderArrow from "./SubFolderArrow"

const StyledDrawer = styled(Drawer)`
  .MuiPaper-root {
    padding-top: ${HEADER_HEIGHT_M}px;
    padding-bottom: 32px;
    background-color: ${(p) => p.theme.colors.headerColor};
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.25);
  }
  .MuiBackdrop-root {
    background-color: rgba(0, 0, 0, 0.4);
    height: 100vh;
  }
  z-index: 1150 !important;
`
const StyledDrawerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
`
const StyledDivider = styled(Divider)`
  width: 100%;
  background-color: ${(p) => p.theme.colors.darkGrey};
  height: 0.5px;
`
const StyledLinkButton = styled(Button)<{
  $selected?: boolean
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background: none;
  border: none;
  width: 100%;
  p {
    color: ${(p) =>
      p.$selected ? p.theme.colors.white : p.theme.colors.borderGrey};
    font-weight: ${(p) => (p.$selected ? "bold" : "normal")};
    text-decoration: none !important;
  }
  &:hover {
    p {
      text-decoration: none;
    }
    background: none;
    border: none;
  }
  padding: 10px 20px;
  justify-content: space-between;
`
const StyledTypographyRtl = styled(TypographyRtl)<{ $underlined?: boolean }>`
  padding-right: 20px;
  ${(p) =>
    p.$underlined &&
    `
    ::after {
      content: "";
      display: block;
      width: 100%;
      height: 2px;
      background-color: ${p.theme.colors.white};
      position: absolute;
      bottom: 0;
      left: 0;
      border-radius: 200px;
    }
    position: relative;
    width: fit-content;
    margin: 0 20px 14px;
    font-weight: 400;
    font-size: 16px;
    leading-trim: NONE;
    line-height: 19px;
    letter-spacing: 0.05em;
    text-align: center;
    vertical-align: middle;
    padding: 0;
    `}
`
const StyledLinkBtn = styled(ButtonBaseBlank)<{ $selected?: boolean }>`
  padding: 14px 30px 14px 0px;
  display: flex !important;
  width: 100%;
  align-items: center;
  justify-content: flex-start;
  &::before {
    content: "•";
    display: inline-block;
    margin-left: 8px;
    font-size: 1.5em;
    color: ${(p) => p.theme.colors.borderGrey};
  }
`

const StyledTypography = styled(Typography)`
  font-size: 14px;
`

const StyledModelWrap = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  width: 95px;
  overflow: hidden;
`
const StyledModel = styled(Box).attrs({ component: "ul" })`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 5px 20px;
  justify-items: center;
  align-items: center;
  width: 100%;
  overflow: hidden;
  padding: 0 20px;
  margin: 10px 0 20px;
`
const StyledModelLi = styled(Box).attrs({ component: "li" })`
  height: 100%;
`
const StyledImage = styled(Image)`
  flex-shrink: 0;
  width: 130px !important;
  max-width: 130px !important;
  inset-inline-start: calc((95px - 130px) / 2) !important;
`

type SideDrawerProps = {
  isOpen: boolean
  toggleDrawer: (
    open: boolean
  ) => (event: React.KeyboardEvent | React.MouseEvent) => void
}

const SideDrawer = ({ isOpen, toggleDrawer }: SideDrawerProps) => {
  const [open, setOpen] = React.useState<string | false>(false)
  // Get the modelList from GlobalContext for later use
  const { modelList } = useGlobalContext()

  const handleClick = (panel: string) => {
    setOpen(panel !== open ? panel : false)
  }

  React.useEffect(() => {
    if (isOpen) {
      return
    }
    setOpen(false)
  }, [isOpen])

  // Model data is available here in the 'modelList' variable
  // We'll use it later for rendering

  return (
    <StyledDrawer anchor={"top"} open={isOpen} onClose={toggleDrawer(false)}>
      <StyledDrawerWrapper role="presentation">
        <StyledDivider />
        <HeaderTopButtons closeDrawer={toggleDrawer(false)} />
        {modelList && modelList.length > 0 && (
          <StyledModel>
            {modelList.map((item, index) => (
              <StyledModelLi key={index}>
                <NextLink href={item.link} onClick={toggleDrawer(false)}>
                  <StyledModelWrap>
                    <StyledImage
                      src={item.image?.imageUrl}
                      alt={item.image?.alt}
                      width={120}
                      height={90}
                      objectFit="contain"
                    />
                    <StyledTypography
                      align="center"
                      variant="body2"
                      color="secondary"
                    >
                      {item.name}
                    </StyledTypography>
                  </StyledModelWrap>
                </NextLink>
              </StyledModelLi>
            ))}
          </StyledModel>
        )}
        <StyledCol $gap="20px" $notCenter $fullWidth $mt="12px">
          {SideDrawerMobile.map((item, index) => (
            <div key={index}>
              {item.links.length === 1 ? (
                <>
                  <NextLink
                    href={item.links[0].link}
                    openInNewTab={item.links[0].blank}
                  >
                    <StyledTypographyRtl
                      variant="body1"
                      $color={
                        item.title === open ? colors.white : colors.borderGrey
                      }
                      $underlined={item.title === "לכל הדגמים"}
                      onClick={toggleDrawer(false)}
                      $fontWeight={item.title === open ? "bold" : "normal"}
                    >
                      {item.title}
                    </StyledTypographyRtl>
                  </NextLink>
                </>
              ) : (
                <>
                  <StyledLinkButton
                    onClick={() => {
                      handleClick(item.title)
                    }}
                    $selected={item.title === open}
                    disableFocusRipple={false}
                    disableRipple={false}
                    aria-expanded={item.title === open ? true : false}
                  >
                    <TypographyRtl variant="body1" $color={colors.borderGrey}>
                      {item.title}
                    </TypographyRtl>
                    <SubFolderArrow open={item.title === open} />
                  </StyledLinkButton>
                  <Collapse
                    in={item.title === open}
                    timeout="auto"
                    unmountOnExit
                  >
                    {item.links.map((item, index) => (
                      <NextLink href={item.link} passHref key={index}>
                        <StyledLinkBtn onClick={toggleDrawer(false)}>
                          <TypographyRtl
                            variant="body2"
                            $color={colors.borderGrey}
                          >
                            {item.title}
                          </TypographyRtl>
                        </StyledLinkBtn>
                      </NextLink>
                    ))}
                  </Collapse>
                </>
              )}
            </div>
          ))}

          {/* Models section will be added here later */}
          {/* The modelList data is already available from GlobalContext */}
        </StyledCol>
      </StyledDrawerWrapper>
    </StyledDrawer>
  )
}

export default SideDrawer
